<template>
  <div>
    <div class="modal fade" tabindex="-1" :class="{ show: showModal }">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h6 class="modal-title">Attendance Check In</h6>
            <button
              type="button"
              class="btn btn-light"
              style="float: right; padding-bottom: 0px"
              @click="closeModal()"
              aria-label="Close"
            >
              <i class="pi pi-times"></i>
            </button>
          </div>
          <Form class="form" @submit="logAttendance">
            <div class="modal-body">
              <div style="width: 100%; position: relative;">
                <div id="map" style="width: 100%; height: 320px;"></div>

                <!-- Loader overlay -->
                <div v-if="isLocating" class="map-loader">
                  <div class="spinner-border" role="status" aria-hidden="true"></div>
                  <div class="mt-2 small">Fetching your location...</div>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <div class="row">
                <div class="col-9" style="padding-right: 0px;">
                  <button
                    type="submit"
                    class="btn btn-custom-primary col-12"
                    :disabled="attendanceLogIsLoading || isLocating"
                  >
                    <span
                      v-if="attendanceLogIsLoading"
                      class="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    <span v-if="!attendanceLogIsLoading">
                      Check-In
                      <small
                        style="margin-left: 10px; position: relative; top: 2.5px; float: right"
                      >{{ currentTime }}</small>
                    </span>
                  </button>
                </div>
                <div class="col-3">
                  <button
                    class="btn btn-custom-secondary col-12"
                    type="button"
                    @click="closeModal()"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';

import { Field, Form, ErrorMessage, defineRule } from 'vee-validate';
import { REGISTER_ATTENDANCE_LOG, REGISTER_ATTENDANCE_SHIFT } from '@/store/action_types';
import { AttendanceLogInputType, AttendanceLogType, EmployeeRoles } from '@/utils/enums';
import { TOAST_GLOBAL_CONFIG, DAYS_OF_THE_WEEK } from '@/utils/const';

defineRule('required', (value, [fieldName = 'Field']) => {
  if (!value || value.length === 0) {
    return `${fieldName} is required`;
  }
  return true;
});

export default {
  props: {
    employeeId: String,
    loggedBy: String
  },
  components: {
    Field,
    Form,
    ErrorMessage,
  },
  data() {
    return {
      DAYS_OF_THE_WEEK,
      showModal: true,
      EmployeeRoles,
      isLocating: true,
      addAttendanceLogObj: {
        inputType: AttendanceLogInputType.application,
        type: AttendanceLogType.checkIn,
        logTime: null,
        latitude: null,
        longitude: null,
        locationName: null,
        loggedBy: null,
        employeeId: null,
        organizationId: null,
      },
      map: null,
      marker: null,
      currentTime: this.getCurrentTime()
    };
  },
  watch: {
    showModal(newVal) {
      if (newVal) {
        setTimeout(() => {
          if (this.map) {
            this.map.invalidateSize();
          }
        }, 500);
      }
    },
  },
  computed: {
    attendanceLog() {
      return this.$store.getters.attendanceLog.data;
    },
    attendanceLogIsLoading() {
      return this.$store.getters.attendanceLog.isLoading;
    },
    attendanceShift() {
      return this.$store.getters.attendanceShift.data;
    },
    attendanceShiftIsLoading() {
      return this.$store.getters.attendanceShift.isLoading;
    },
    account() {
      return this.$store.getters.account.data;
    },
  },
  methods: {
    setValues() {
      this.addAttendanceLogObj.employeeId = this.employeeId;
      this.addAttendanceLogObj.organizationId = this.account.organizationId;
    },
    closeModal() {
      this.$emit('closeAttendanceCheckinModal');
    },
    getCurrentTime() {
      const now = new Date();
      let hours = now.getHours();
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      return `${hours.toString().padStart(2, '0')}:${minutes} ${ampm}`;
    },
    initializeMap() {
      this.$nextTick(() => {
        this.isLocating = true;
        this.map = L.map('map', {
          center: [8.9806, 38.7578],
          zoom: 13,
          maxZoom: 18,
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; OpenStreetMap contributors',
        }).addTo(this.map);

        this.map.on('click', this.onMapClick);

        const locationIcon = L.icon({
          iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
          shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          shadowSize: [41, 41],
        });

        const BEST_TARGET_ACCURACY = 25;
        const SAMPLE_WINDOW_MS = 8000;
        let best = null;
        let accuracyCircle = null;
        let zoomed = false;

        if (!('geolocation' in navigator)) {
          console.warn('Geolocation not supported');
          this.isLocating = false;
        } else {
          const startedAt = Date.now();
          const watchId = navigator.geolocation.watchPosition(
            (pos) => {
              const { latitude, longitude, accuracy } = pos.coords;
              this.addAttendanceLogObj.latitude = latitude;
              this.addAttendanceLogObj.longitude = longitude;

              const latlng = L.latLng(latitude, longitude);

              if (!best || (accuracy && accuracy < best.accuracy)) {
                best = { latlng, accuracy };

                if (this.marker) this.marker.setLatLng(latlng);
                else this.marker = L.marker(latlng, { icon: locationIcon }).addTo(this.map);

                if (this.isLocating) this.isLocating = false;
                this.focusOnMarker(17);

                if (accuracyCircle) accuracyCircle.setLatLng(latlng).setRadius(accuracy || 50);
                else accuracyCircle = L.circle(latlng, { radius: accuracy || 50 }).addTo(this.map);
              }

              if (accuracy && accuracy <= BEST_TARGET_ACCURACY && !zoomed) {
                zoomed = true;
                this.map.flyTo(latlng, 16);
                navigator.geolocation.clearWatch(watchId);
              }

              if (Date.now() - startedAt > SAMPLE_WINDOW_MS) {
                navigator.geolocation.clearWatch(watchId);
                if (best && !zoomed) {
                  this.map.flyTo(best.latlng, best.accuracy <= 80 ? 16 : 14);
                }
              }
            },
            (err) => {
              console.warn('Geolocation error:', err.message);
              this.isLocating = false;
            },
            {
              enableHighAccuracy: true,
              timeout: SAMPLE_WINDOW_MS + 4000,
              maximumAge: 0,
            }
          );
        }

        setTimeout(() => {
          if (this.map) this.map.invalidateSize();
        }, 300);
      });
    },
    focusOnMarker(zoom = 17) {
      if (this.map && this.marker) {
        const ll = this.marker.getLatLng();
        this.map.setView(ll, Math.max(this.map.getZoom(), zoom), { animate: true });
      }
    },
    async reverseGeocode(lat, lon) {
      const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`;
      const { data } = await axios.get(url);
      if (data && data.display_name) {
        return data.display_name;
      }
    },
    async logAttendance() {
      if(this.loggedBy) {
        this.addAttendanceLogObj.loggedBy = this.loggedBy
      }
      this.addAttendanceLogObj.logTime = new Date(new Date().toISOString());
      
      this.$store.dispatch(REGISTER_ATTENDANCE_LOG, this.addAttendanceLogObj).then((res) => {
        if (res) {
          this.closeModal();
          this.$emit('refreshAttendance');
        }
      });
    },
  },
  async mounted() {
    this.setValues();
    if (this.showModal) {
      this.initializeMap();
    }
    this._currentTimeInterval = setInterval(() => {
      this.currentTime = this.getCurrentTime();
    }, 60000);

    window.addEventListener('message', async (e) => {
      if (e.data?.type === 'nativeLocation') {
        const { lat, lng, accuracy } = e.data;
        const latlng = L.latLng(lat, lng);

        if (this.marker) this.marker.setLatLng(latlng);
        else this.marker = L.marker(latlng, { icon: this.locationIcon }).addTo(this.map);

        this.focusOnMarker(18);
        this.map.flyTo(latlng, 18);
        this.addAttendanceLogObj.latitude = lat;
        this.addAttendanceLogObj.longitude = lng;
        this.addAttendanceLogObj.accuracy = Math.round(accuracy);
        this.isLocating = false;
      }
    });
  },
  beforeUnmount() {
    if (this._currentTimeInterval) {
      clearInterval(this._currentTimeInterval);
    }
  },
};
</script>

<style scoped>
.modal-content {
  border-radius: 20px !important;
  border: 1px solid transparent;
  background-color: #fdfdfe !important;
  max-height: 92vh;
  overflow-x: hidden;
  overflow-y: auto;
}

.modal {
  display: none;
}

.modal.show {
  display: block;
}

.modal-dialog {
  max-width: 92%;
}

.modal {
  background-color: rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

.modal-content {
  border: 1px solid #e4e4e7;
}

.modal-header {
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  border-bottom: 1px solid #e4e4e7;
}

.modal-header .modal-title {
  position: relative;
  top: 8px;
  font-size: 1.1rem;
}

.modal-content .alert {
  margin: 10px 10px 0px 10px;
  font-size: 0.85rem;
}

.modal-footer {
  border-top: 1px solid #e4e4e7;
  display: block !important;
  justify-content: unset !important;
  align-items: unset !important;
  padding-left: 0px;
  padding-right: 0px;
}

.map-loader {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.75);
  z-index: 1000;
  border-radius: 8px;
}

@media (min-width: 1000px) {
  .modal-dialog {
    max-width: 45%;
  }
}
</style>
